import { supabase } from './supabase';
import type { Database } from '../types/database.types';
import { logger } from '../utils/logger';

type TbmMessage = Database['public']['Tables']['tbm_messages']['Row'];
type TbmConfirmation = Database['public']['Tables']['tbm_confirmations']['Row'];
type User = Database['public']['Tables']['users']['Row'];
type Company = Database['public']['Tables']['companies']['Row'];
type Branch = Database['public']['Tables']['branches']['Row'];

export interface NoticeWithRelations extends TbmMessage {
  company?: Company | null;
  branch?: Branch | null;
  created_by_user?: User | null;
  confirmations?: TbmConfirmation[];
  total_recipients?: number;
  read_count?: number;
  read_rate?: number;
  unreadUsers?: Array<{
    userId: string;
    userName: string;
    branchName: string;
    phoneNumber: string;
  }>;
}

export interface NoticeFilters {
  companyId?: string;
  branchId?: string;
  isSent?: boolean;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  targetType?: string;
  noticeType?: 'TBM' | 'NOTICE';
}

export interface NoticeCreateData {
  title: string;
  content: string;
  targetType: 'all' | 'company' | 'branch' | 'team';
  companyId?: string;
  branchId?: string;
  targetDate: string;
  scheduledAt?: string;
  attachments?: string[];
  noticeType: 'TBM' | 'NOTICE';
}


export interface NoticeStatistics {
  totalSent: number;
  totalRead: number;
  readRate: number;
  unreadUsers: Array<{
    userId: string;
    userName: string;
    branchName: string;
    phoneNumber: string;
  }>;
}

export class NoticeService {
  // 공지사항 목록 조회 (통계 제외 옵션 추가)
  static async getNotices(filters?: NoticeFilters, currentUser?: User | null, includeStats: boolean = false) {
    try {
      let query = supabase
        .from('tbm_messages')
        .select(`
          *,
          company:companies(id, name, company_code),
          branch:branches(id, branch_name, branch_code),
          created_by_user:users!tbm_messages_created_by_fkey(id, name, username)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (filters?.companyId) {
        query = query.eq('company_id', filters.companyId);
      }

      if (filters?.branchId) {
        query = query.eq('branch_id', filters.branchId);
      }

      if (filters?.isSent !== undefined) {
        query = query.eq('is_sent', filters.isSent);
      }

      if (filters?.searchTerm) {
        query = query.or(`title.ilike.%${filters.searchTerm}%,content.ilike.%${filters.searchTerm}%`);
      }

      if (filters?.startDate && filters?.endDate) {
        query = query.gte('created_at', filters.startDate).lte('created_at', filters.endDate);
      }

      if (filters?.targetType) {
        query = query.eq('target_type', filters.targetType);
      }

      if (filters?.noticeType) {
        query = query.eq('notice_type', filters.noticeType);
      }

      logger.debug('NoticeService.getNotices 쿼리 실행 전', { filters });
      const { data, error } = await query;

      if (error) throw error;

      logger.debug('DB에서 가져온 전체 공지사항', { count: data?.length });
      logger.debug('최근 공지사항 5개', { notices: data?.slice(0, 5).map(n => ({
        id: n.id,
        title: n.title,
        is_sent: n.is_sent,
        sent_at: n.sent_at,
        scheduled_at: n.scheduled_at,
        created_at: n.created_at,
        is_active: n.is_active
      })) });

      // 사용자 권한에 따른 필터링 - 모바일 앱 기준
      let filteredData = data || [];
      if (currentUser && !currentUser.is_admin) {
        logger.debug('일반 관리자 필터링 시작', { currentUser: { id: currentUser?.id, name: currentUser?.name } });
        // 일반 관리자는 자신의 회사/지점 공지만 볼 수 있음
        filteredData = filteredData.filter(notice => {
          // 모바일 앱 기준:
          // 1. 전체 공지사항 (company_id와 branch_id가 모두 null)
          if (!notice.company_id && !notice.branch_id) {
            return true;
          }
          // 2. 자신의 회사 전체 대상 TBM (company_id 일치, branch_id가 null)
          if (currentUser.company_id && notice.company_id === currentUser.company_id && !notice.branch_id) {
            return true;
          }
          // 3. 자신의 지점 대상 TBM (branch_id 일치)
          if (currentUser.branch_id && notice.branch_id === currentUser.branch_id) {
            return true;
          }
          return false;
        });
        logger.debug('필터링 후 공지사항', { count: filteredData.length });
      }

      // 통계 정보는 필요한 경우에만 추가
      if (includeStats) {
        // 발송된 공지사항 ID들만 추출
        const sentNoticeIds = filteredData
          .filter(n => n.is_sent)
          .map(n => n.id);

        // 통계 정보 일괄 조회
        const statsMap = await this.getBatchNoticeStatistics(sentNoticeIds);
        
        // 공지사항에 통계 정보 매핑
        const noticesWithStats = filteredData.map(notice => {
          if (!notice.is_sent) {
            return {
              ...notice,
              total_recipients: 0,
              read_count: 0,
              read_rate: 0
            } as NoticeWithRelations;
          }
          
          const stats = statsMap[notice.id] || { totalSent: 0, totalRead: 0, readRate: 0 };
          return {
            ...notice,
            total_recipients: stats.totalSent,
            read_count: stats.totalRead,
            read_rate: stats.readRate
          } as NoticeWithRelations;
        });
        
        return noticesWithStats;
      }
      
      // 통계 없이 기본 정보만 반환
      return filteredData.map(notice => ({
        ...notice,
        total_recipients: 0,
        read_count: 0,
        read_rate: 0
      } as NoticeWithRelations));
    } catch (error) {
      logger.error('Error fetching notices', error, { filters });
      throw error;
    }
  }

  // 단일 공지사항 조회
  static async getNotice(noticeId: string) {
    try {
      const { data, error } = await supabase
        .from('tbm_messages')
        .select(`
          *,
          company:companies(id, name, company_code),
          branch:branches(id, branch_name, branch_code),
          created_by_user:users!tbm_messages_created_by_fkey(id, name, username)
        `)
        .eq('id', noticeId)
        .single();

      if (error) throw error;

      const stats = await this.getNoticeStatistics(noticeId);
      return {
        ...data,
        total_recipients: stats.totalSent,
        read_count: stats.totalRead,
        read_rate: stats.readRate,
        unreadUsers: stats.unreadUsers
      } as NoticeWithRelations;
    } catch (error) {
      logger.error('Error fetching notice', error, { id: noticeId });
      throw error;
    }
  }

  // 공지사항 생성
  static async createNotice(noticeData: NoticeCreateData, createdBy: string) {
    try {
      const insertData: any = {
        title: noticeData.title,
        content: noticeData.content,
        target_type: noticeData.targetType,
        target_date: noticeData.targetDate,
        created_by: createdBy,
        is_active: true,
        is_sent: false,
        notice_type: noticeData.noticeType || 'TBM'
      };

      if (noticeData.companyId) {
        insertData.company_id = noticeData.companyId;
      }

      if (noticeData.branchId) {
        insertData.branch_id = noticeData.branchId;
      }

      if (noticeData.scheduledAt) {
        insertData.scheduled_at = noticeData.scheduledAt;
      }

      const { data, error } = await supabase
        .from('tbm_messages')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error creating notice', error);
      throw error;
    }
  }

  // 공지사항 수정
  static async updateNotice(noticeId: string, noticeData: Partial<NoticeCreateData>) {
    try {
      const updateData: any = {};

      if (noticeData.title !== undefined) updateData.title = noticeData.title;
      if (noticeData.content !== undefined) updateData.content = noticeData.content;
      if (noticeData.targetType !== undefined) updateData.target_type = noticeData.targetType;
      if (noticeData.targetDate !== undefined) updateData.target_date = noticeData.targetDate;
      if (noticeData.companyId !== undefined) updateData.company_id = noticeData.companyId;
      if (noticeData.branchId !== undefined) updateData.branch_id = noticeData.branchId;
      if (noticeData.scheduledAt !== undefined) updateData.scheduled_at = noticeData.scheduledAt;
      if (noticeData.noticeType !== undefined) updateData.notice_type = noticeData.noticeType;

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('tbm_messages')
        .update(updateData)
        .eq('id', noticeId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error updating notice', error, { id: noticeId });
      throw error;
    }
  }

  // 공지사항 삭제 (소프트 삭제)
  static async deleteNotice(noticeId: string) {
    try {
      const { data, error } = await supabase
        .from('tbm_messages')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', noticeId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error deleting notice', error, { id: noticeId });
      throw error;
    }
  }

  // 공지사항 발송
  static async sendNotice(noticeId: string) {
    try {
      const { data, error } = await supabase
        .from('tbm_messages')
        .update({ 
          is_sent: true,
          sent_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', noticeId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error sending notice', error, { id: noticeId });
      throw error;
    }
  }

  // 일괄 통계 조회 (성능 최적화)
  static async getBatchNoticeStatistics(noticeIds: string[]): Promise<Record<string, NoticeStatistics>> {
    if (noticeIds.length === 0) return {};
    
    try {
      // 1. 모든 공지사항 정보 일괄 조회
      const { data: notices, error: noticeError } = await supabase
        .from('tbm_messages')
        .select('id, target_type, company_id, branch_id')
        .in('id', noticeIds);

      if (noticeError) throw noticeError;
      
      // 2. 모든 확인 정보 일괄 조회 (GROUP BY를 사용하여 집계)
      const { data: confirmations, error: confirmError } = await supabase
        .from('tbm_confirmations')
        .select('tbm_id')
        .in('tbm_id', noticeIds);
        
      if (confirmError) throw confirmError;
      
      // 3. 확인 수를 미리 집계
      const confirmationCounts: Record<string, number> = {};
      confirmations?.forEach(conf => {
        confirmationCounts[conf.tbm_id] = (confirmationCounts[conf.tbm_id] || 0) + 1;
      });
      
      // 4. 회사/지점별 사용자 수 캐시 (중복 쿼리 방지)
      const userCountCache: Record<string, number> = {};
      
      // 5. 통계 계산
      const statsMap: Record<string, NoticeStatistics> = {};
      
      for (const notice of notices || []) {
        let totalRecipients = 0;
        
        // 캐시 키 생성
        let cacheKey = 'all';
        if (notice.target_type === 'company' && notice.company_id) {
          cacheKey = `company_${notice.company_id}`;
        } else if (notice.target_type === 'branch' && notice.branch_id) {
          cacheKey = `branch_${notice.branch_id}`;
        }
        
        // 캐시에서 확인하거나 새로 조회
        if (userCountCache[cacheKey] !== undefined) {
          totalRecipients = userCountCache[cacheKey];
        } else {
          let recipientQuery = supabase
            .from('users')
            .select('id', { count: 'exact' })
            .eq('is_active', true)
            .eq('is_deleted', false);

          if (notice.target_type === 'company' && notice.company_id) {
            recipientQuery = recipientQuery.eq('company_id', notice.company_id);
          } else if (notice.target_type === 'branch' && notice.branch_id) {
            recipientQuery = recipientQuery.eq('branch_id', notice.branch_id);
          }

          const { count } = await recipientQuery;
          totalRecipients = count || 0;
          userCountCache[cacheKey] = totalRecipients;
        }
        
        // 읽음 확인 수 계산
        const readCount = confirmationCounts[notice.id] || 0;
        const readRate = totalRecipients ? Math.round((readCount / totalRecipients) * 100) : 0;
        
        statsMap[notice.id] = {
          totalSent: totalRecipients,
          totalRead: readCount,
          readRate,
          unreadUsers: []
        };
      }
      
      return statsMap;
    } catch (error) {
      logger.error('Error fetching batch statistics', error, { noticeIds });
      return {};
    }
  }

  // 공지사항 통계 조회
  static async getNoticeStatistics(noticeId: string): Promise<NoticeStatistics> {
    try {
      // 공지사항 정보 조회
      const { data: notice, error: noticeError } = await supabase
        .from('tbm_messages')
        .select('target_type, company_id, branch_id')
        .eq('id', noticeId)
        .single();

      if (noticeError) throw noticeError;

      // 대상 사용자 수 계산
      let recipientQuery = supabase
        .from('users')
        .select('id', { count: 'exact' })
        .eq('is_active', true)
        .eq('is_deleted', false);

      if (notice.target_type === 'company' && notice.company_id) {
        recipientQuery = recipientQuery.eq('company_id', notice.company_id);
      } else if (notice.target_type === 'branch' && notice.branch_id) {
        recipientQuery = recipientQuery.eq('branch_id', notice.branch_id);
      }

      const { count: totalRecipients } = await recipientQuery;

      // 읽음 확인 수 조회
      const { count: readCount } = await supabase
        .from('tbm_confirmations')
        .select('id', { count: 'exact' })
        .eq('tbm_id', noticeId);

      const totalSent = totalRecipients || 0;
      const totalRead = readCount || 0;
      const readRate = totalSent > 0 ? Math.round((totalRead / totalSent) * 100) : 0;

      // 미확인자 명단 조회
      const { data: confirmedUsers } = await supabase
        .from('tbm_confirmations')
        .select('user_id')
        .eq('tbm_id', noticeId);

      const confirmedUserIds = confirmedUsers?.map(c => c.user_id) || [];

      let unreadQuery = supabase
        .from('users')
        .select(`
          id,
          name,
          phone_number,
          branch:branches(branch_name)
        `)
        .eq('is_active', true)
        .eq('is_deleted', false);

      if (confirmedUserIds.length > 0) {
        unreadQuery = unreadQuery.not('id', 'in', `(${confirmedUserIds.join(',')})`);
      }

      if (notice.target_type === 'company' && notice.company_id) {
        unreadQuery = unreadQuery.eq('company_id', notice.company_id);
      } else if (notice.target_type === 'branch' && notice.branch_id) {
        unreadQuery = unreadQuery.eq('branch_id', notice.branch_id);
      }

      const { data: unreadUsers } = await unreadQuery;

      return {
        totalSent,
        totalRead,
        readRate,
        unreadUsers: (unreadUsers || []).map(user => ({
          userId: user.id,
          userName: user.name,
          branchName: user.branch?.branch_name || '',
          phoneNumber: user.phone_number || ''
        }))
      };
    } catch (error) {
      logger.error('Error fetching notice statistics', error, { noticeId });
      return {
        totalSent: 0,
        totalRead: 0,
        readRate: 0,
        unreadUsers: []
      };
    }
  }

}